import { useActiveDepartment } from "@/hooks/useActiveDepartment";
import { useRootLoader } from "@/hooks/useRootLoader";
import { useTenantFeatureFlag } from "@/hooks/useTenantFeatureFlag";
import {
  checkCanInActiveDept,
  checkCanOpenManagerSwitch,
  checkCanViewTeamSummary,
} from "@/utils/role";

export interface ManagerAccess {
  /** マネージャー画面への切替（名前の行の右端の「⇄ マネージャー画面」）を出すか */
  canOpen: boolean;
  /** 活動サマリ（部の数字）を見られるか（HOME に活動サマリを選べるか・未設定なら活動サマリか） */
  canViewSummary: boolean;
  /** いまの部署の配信の設定を変えられるか（managerRole ON のテナントの部署ごとの配信設定） */
  canManageDelivery: boolean;
}

/**
 * マネージャー画面に関わる出し分けの**唯一の入口**（PRODUCT_DEV-1092・1070）。判定の中身は `utils/role.ts`。
 *
 * グロナビ・HOME・マイページ・マネージャー画面が同じ判定を使うためにまとめた。画面ごとに `isManager` を
 * 決めると、どれか1つだけ古い条件で入口を出す（または消す）。
 *
 * ⚠ どれも**いまの部署**の役割で決める（`checkCanInActiveDept`）。人単位の `isManager`（どこかの部署で
 * manager）で決めると、manager をしていない部署へ切り替えても活動サマリが HOME のまま・切替が出たままになる
 * （2026-10-08 dev で踏んだ）。切替は manager だけで、manager でない admin には出さない（2026-10-09 決定）。
 * マネージャー画面の**入室**判定（admin も入れる）は `MyPage/Admin` の `checkCanOpenAdminView` で、ここではない。
 */
export const useManagerAccess = (): ManagerAccess => {
  const { auth, isManager } = useRootLoader();
  const roleSplit = useTenantFeatureFlag("managerRole");
  const { active } = useActiveDepartment();
  if (!auth) return { canOpen: false, canViewSummary: false, canManageDelivery: false };
  const viewCtx = { roleSplit, isManager: checkCanInActiveDept(active, isManager, "team.view") };
  return {
    canOpen: checkCanOpenManagerSwitch(auth, viewCtx),
    canViewSummary: checkCanViewTeamSummary(auth, viewCtx),
    canManageDelivery: checkCanInActiveDept(active, isManager, "team.manageDelivery"),
  };
};
